import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCheck, Loader2 } from "lucide-react";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * One-click "approve all pending" button. Calls a backend bulk endpoint
 * and triggers `onDone()` to let the parent reload its data.
 *
 * @param {string} resource — one of: orders, debt-payments, cash-submissions, payment-confirmations, withdrawals
 * @param {string} label   — Arabic label (e.g. "موافقة على كل الطلبات")
 * @param {function} onDone — callback after successful bulk approve
 * @param {number} count   — current pending count (button is disabled when 0)
 */
const BulkApproveButton = ({ resource, label = "موافقة على الكل", onDone, count = 0 }) => {
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (busy || count === 0) return;
    if (!(await confirmDialog(`سيتم الموافقة على ${count} عنصر دفعة واحدة. متأكد؟`))) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/bulk-approve/${resource}`);
      const n = data?.approved || 0;
      if (n > 0) toast.success(`تمت الموافقة على ${n} عنصر`);
      else toast.info("لا يوجد عنصر مؤهّل للموافقة");
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشلت العملية");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={run}
      disabled={busy || count === 0}
      data-testid={`bulk-approve-${resource}`}
      className="bg-[#15803d] hover:bg-[#126b32] disabled:bg-[#9CA3AF] disabled:cursor-not-allowed text-white font-bold py-2 px-3 rounded-full flex items-center gap-1 text-xs sm:text-sm transition-colors shadow-sm"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCheck className="w-4 h-4" />}
      {label}
      {count > 0 && (
        <span className="bg-white text-[#15803d] rounded-full px-1.5 py-0.5 text-[10px] font-black ml-1" dir="ltr">
          {count}
        </span>
      )}
    </button>
  );
};

export default BulkApproveButton;
